/**
 * ScreenEffects — small display-referred post nodes: vignette, lens
 * distortion (barrel/pincushion), and lift-gamma-gain parametric grading.
 *
 * Each factory returns `{ node, uniforms }` (the ContactShadows/GodRays house
 * pattern) so callers can drive the effect per-frame without a pipeline
 * rebuild. CPU mirrors of the per-pixel math are exported for unit tests.
 *
 * References:
 * - Vignette: radial smoothstep falloff from screen centre (standard form).
 * - Lens distortion: Brown–Conrady style radial term — uv' = c + (uv−c)·(1 +
 *   k1·r² + k2·r⁴); k>0 barrel, k<0 pincushion. Resamples the input via
 *   convertToTexture, so it must sit AFTER passes whose output is an
 *   expression, and after AA (distorting first would alias the warped edges).
 * - Grading: classic filmic lift/gamma/gain (per-channel):
 *   out = pow(max(gain·(x + lift·(1−x)), 0), 1/gamma) — the widely used
 *   LGG mapping (ASC-CDL's slope/offset/power is the same family with
 *   offset = lift·gain and power = 1/gamma).
 */

import type * as THREE from 'three/webgpu';
import {
  Fn, uniform, float, vec2, vec3, vec4, uv, smoothstep, pow, max, min, mix,
  convertToTexture, screenSize, clamp,
} from 'three/tsl';

// ─── CPU reference math (exported for tests) ────────────────────────────────

/** Vignette multiplier at normalized screen uv (0..1). */
export function vignetteFactorCpu(
  u: number, v: number, radius: number, softness: number, strength: number,
): number {
  const dx = u - 0.5;
  const dy = v - 0.5;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const t = clamp01((dist - radius) / Math.max(softness, 1e-6));
  const s = t * t * (3 - 2 * t); // smoothstep
  return 1 - s * strength;
}

/** Brown–Conrady radial distortion of a centred uv. */
export function distortUvCpu(
  u: number, v: number, k1: number, k2: number,
): { u: number; v: number } {
  const cx = u - 0.5;
  const cy = v - 0.5;
  const r2 = cx * cx + cy * cy;
  const f = 1 + k1 * r2 + k2 * r2 * r2;
  return { u: 0.5 + cx * f, v: 0.5 + cy * f };
}

/** Filmic lift/gamma/gain for one channel. */
export function liftGammaGainCpu(
  x: number, lift: number, gamma: number, gain: number,
): number {
  const lifted = x + lift * (1 - x);
  const gained = Math.max(gain * lifted, 0);
  return Math.pow(gained, 1 / Math.max(gamma, 1e-4));
}

function clamp01(x: number): number { return x < 0 ? 0 : (x > 1 ? 1 : x); }

// ─── Vignette ───────────────────────────────────────────────────────────────

export interface VignetteConfig {
  /** Distance from centre where darkening begins (default 0.35). */
  radius?: number;
  /** Falloff width beyond the radius (default 0.45). */
  softness?: number;
  /** Maximum darkening 0..1 at the corners (default 0.5). */
  strength?: number;
  enabled?: boolean;
}

export interface VignetteUniforms {
  radius: { value: number };
  softness: { value: number };
  strength: { value: number };
}

export function createVignetteNode(
  input: unknown,
  config?: VignetteConfig,
): { node: unknown; uniforms: VignetteUniforms } {
  const uRadius = uniform(config?.radius ?? 0.35);
  const uSoftness = uniform(config?.softness ?? 0.45);
  const uStrength = uniform(config?.strength ?? 0.5);
  const build = Fn(() => {
    const centered = uv().sub(vec2(0.5, 0.5));
    const dist = centered.length();
    const fall = smoothstep(uRadius, uRadius.add(uSoftness), dist);
    const factor = float(1).sub(fall.mul(uStrength));
    const c = vec4(input as never);
    return vec4(c.rgb.mul(factor), c.a);
  });
  return {
    node: build(),
    uniforms: {
      radius: uRadius as unknown as { value: number },
      softness: uSoftness as unknown as { value: number },
      strength: uStrength as unknown as { value: number },
    },
  };
}

// ─── Lens distortion ────────────────────────────────────────────────────────

export interface LensDistortionConfig {
  /** Primary radial coefficient — >0 barrel, <0 pincushion (default 0.08). */
  k1?: number;
  /** Secondary radial coefficient (default 0). */
  k2?: number;
  /** Uniform zoom compensating edge stretch (default 1.0). */
  scale?: number;
  enabled?: boolean;
}

export interface LensDistortionUniforms {
  k1: { value: number };
  k2: { value: number };
  scale: { value: number };
}

export function createLensDistortionNode(
  input: unknown,
  config?: LensDistortionConfig,
): { node: unknown; uniforms: LensDistortionUniforms } {
  const uK1 = uniform(config?.k1 ?? 0.08);
  const uK2 = uniform(config?.k2 ?? 0);
  const uScale = uniform(config?.scale ?? 1.0);
  // The warp re-samples the chain output at a displaced uv, which needs a
  // TEXTURE, not an expression — convertToTexture renders the input into an
  // internal RTT first.
  const tex = convertToTexture(input as never);
  const build = Fn(() => {
    const centered = uv().sub(vec2(0.5, 0.5)).div(uScale);
    const r2 = centered.dot(centered);
    const f = float(1).add(uK1.mul(r2)).add(uK2.mul(r2).mul(r2));
    const distorted = vec2(0.5, 0.5).add(centered.mul(f));
    return (tex as unknown as { sample: (n: unknown) => unknown }).sample(distorted);
  });
  return {
    node: build(),
    uniforms: {
      k1: uK1 as unknown as { value: number },
      k2: uK2 as unknown as { value: number },
      scale: uScale as unknown as { value: number },
    },
  };
}

// ─── Sharpen (RCAS-style neighborhood-clamped unsharp) ──────────────────────

export interface SharpenConfig {
  /** Sharpening strength 0..1 (default 0.3). */
  strength?: number;
  enabled?: boolean;
}

export interface SharpenUniforms {
  strength: { value: number };
}

/** CPU mirror: centre-weighted unsharp with neighborhood clamp (one channel). */
export function sharpenCpu(
  c: number, n: number, s: number, e: number, w: number, strength: number,
): number {
  const sharpened = c + (c * 4 - (n + s + e + w)) * strength * 0.25;
  const lo = Math.min(c, n, s, e, w);
  const hi = Math.max(c, n, s, e, w);
  return Math.min(hi, Math.max(lo, sharpened));
}

/**
 * AMD-CAS-family sharpen on the chain output: 4-neighbour unsharp mask whose
 * result is clamped to the neighbourhood min/max so edges crisp up without
 * ringing (the same overshoot guard RCAS uses).
 */
export function createSharpenNode(
  input: unknown,
  config?: SharpenConfig,
): { node: unknown; uniforms: SharpenUniforms } {
  const uStrength = uniform(config?.strength ?? 0.3);
  const tex = convertToTexture(input as never) as unknown as { sample: (n: unknown) => { rgb: unknown; a: unknown } };
  const build = Fn(() => {
    const texel = vec2(1, 1).div(screenSize);
    const u0 = uv();
    const c = vec4(tex.sample(u0) as never);
    const n = vec3(tex.sample(u0.add(vec2(0, texel.y).mul(-1))) as never);
    const s = vec3(tex.sample(u0.add(vec2(0, texel.y))) as never);
    const e = vec3(tex.sample(u0.add(vec2(texel.x, 0))) as never);
    const w = vec3(tex.sample(u0.add(vec2(texel.x, 0).mul(-1))) as never);
    const lo = min(min(min(min(c.rgb, n), s), e), w);
    const hi = max(max(max(max(c.rgb, n), s), e), w);
    const sharpened = c.rgb.add(
      c.rgb.mul(4).sub(n.add(s).add(e).add(w)).mul(uStrength).mul(0.25),
    );
    return vec4(clamp(sharpened, lo, hi), c.a);
  });
  return {
    node: build(),
    uniforms: { strength: uStrength as unknown as { value: number } },
  };
}

// ─── Lift / Gamma / Gain parametric grading ─────────────────────────────────

export interface ColorGradeConfig {
  /** Per-channel lift (shadows), typically −0.5..0.5 (default [0,0,0]). */
  lift?: [number, number, number];
  /** Per-channel gamma (midtones), typically 0.5..2 (default [1,1,1]). */
  gamma?: [number, number, number];
  /** Per-channel gain (highlights), typically 0..2 (default [1,1,1]). */
  gain?: [number, number, number];
  /** Post-grade saturation multiplier (default 1). */
  saturation?: number;
  enabled?: boolean;
}

export interface ColorGradeUniforms {
  lift: { value: THREE.Vector3 };
  gamma: { value: THREE.Vector3 };
  gain: { value: THREE.Vector3 };
  saturation: { value: number };
}

export function createColorGradeNode(
  input: unknown,
  config?: ColorGradeConfig,
): { node: unknown; uniforms: ColorGradeUniforms } {
  const l = config?.lift ?? [0, 0, 0];
  const g = config?.gamma ?? [1, 1, 1];
  const gn = config?.gain ?? [1, 1, 1];
  const uLift = uniform(vec3(l[0], l[1], l[2]));
  const uGamma = uniform(vec3(g[0], g[1], g[2]));
  const uGain = uniform(vec3(gn[0], gn[1], gn[2]));
  const uSat = uniform(config?.saturation ?? 1);
  const build = Fn(() => {
    const c = vec4(input as never);
    const x = c.rgb;
    const lifted = x.add(uLift.mul(float(1).sub(x)));
    const gained = max(lifted.mul(uGain), vec3(0, 0, 0));
    const graded = pow(gained, vec3(1, 1, 1).div(max(uGamma, vec3(1e-4, 1e-4, 1e-4))));
    // Rec. 709 luma for the saturation pivot.
    const luma = graded.dot(vec3(0.2126, 0.7152, 0.0722));
    const sat = mix(vec3(luma, luma, luma), graded, uSat);
    return vec4(sat, c.a);
  });
  return {
    node: build(),
    uniforms: {
      lift: uLift as unknown as { value: THREE.Vector3 },
      gamma: uGamma as unknown as { value: THREE.Vector3 },
      gain: uGain as unknown as { value: THREE.Vector3 },
      saturation: uSat as unknown as { value: number },
    },
  };
}
